import { DropdownItem, NestingParentDropdownItem } from "@app/shared/checkbox-dropdown/types";
import { ZipCoverageSelectedServiceArea } from "@app/projects/zip-projects/maps/interfaces";

/** Suffix for the divider row's key. Not a zip code, so it can never collide with a real leaf key. */
const NO_MEMBERSHIP_DIVIDER_SUFFIX = "__no-membership";

export const NO_MEMBERSHIP_DIVIDER_LABEL = "No Membership";

/** The divider row a county group gets between its zips with membership and its zero-membership zips. */
export const buildNoMembershipDividerKey = (countyKey: string): string =>
  `${countyKey}${NO_MEMBERSHIP_DIVIDER_SUFFIX}`;

/**
 * Applies the "Show Zero Membership" toggle to the Service Area drill-down (WI 210362).
 *
 * The tree is built from geography, so it lists every zip in each service-area county whether or not the analysis
 * found members there. With the toggle off those zips are noise — nothing to draw — so they come out of the list,
 * along with any county left empty by the cut. A state group whose counties all dropped disappears on its own,
 * because the dropdown derives its grandparent tier from the items that survive.
 *
 * With the toggle on, each county lists its zips with membership first, then a "No Membership" divider, then its
 * zero-membership zips. The divider only appears where there is something below it.
 */
export const applyZeroMembershipFilter = (
  items: NestingParentDropdownItem[],
  selectionLookup: Map<string, ZipCoverageSelectedServiceArea>,
  zipCodesWithMembership: Set<string>,
  showZeroMembership: boolean,
): NestingParentDropdownItem[] => {
  const hasMembership = (child: DropdownItem<string>): boolean => {
    const zipCode = selectionLookup.get(child.key)?.zipCode;
    return zipCode !== undefined && zipCodesWithMembership.has(zipCode);
  };

  return items.reduce<NestingParentDropdownItem[]>((accumulator, county) => {
    const withMembership = county.children.filter(hasMembership);

    if (!showZeroMembership) {
      // A county whose zips all have zero membership has nothing left to offer, so it goes too.
      if (withMembership.length > 0) {
        accumulator.push({ ...county, children: withMembership });
      }
      return accumulator;
    }

    const zeroMembership = county.children.filter((child) => !hasMembership(child));
    if (zeroMembership.length === 0) {
      accumulator.push(county);
      return accumulator;
    }

    accumulator.push({
      ...county,
      children: [
        ...withMembership,
        { isDivider: true, key: buildNoMembershipDividerKey(county.key), name: NO_MEMBERSHIP_DIVIDER_LABEL },
        ...zeroMembership,
      ],
    });
    return accumulator;
  }, []);
};

/** True when the selected Service Area key points at a zip the toggle would hide. */
export const isZeroMembershipSelection = (
  selectedKey: string | null,
  selectionLookup: Map<string, ZipCoverageSelectedServiceArea>,
  zipCodesWithMembership: Set<string>,
): boolean => {
  if (!selectedKey) {
    return false;
  }
  const zipCode = selectionLookup.get(selectedKey)?.zipCode;
  return zipCode !== undefined && !zipCodesWithMembership.has(zipCode);
};
